import { useId, useRef } from "react";
import { createPortal } from "react-dom";
import { LoaderCircle } from "lucide-react";
import { t } from "../../i18n";
import { useWorkspaceDialogFocus } from "../workspace-editor/useWorkspaceDialogFocus";

export type GitAction = {
  action: string;
  title: string;
  description: string;
  body?: Record<string, unknown>;
  input?: boolean;
  destructive?: boolean;
};
export function GitActionDialog({
  action,
  value,
  onChange,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  action: GitAction;
  value: string;
  onChange: (value: string) => void;
  busy: boolean;
  error: string;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const ref = useWorkspaceDialogFocus<HTMLFormElement>({
    onClose: () => {
      if (!busy) onClose();
    },
    initialFocusRef: input,
  });
  return createPortal(
    <div
      className="fixed inset-0 z-[90] grid place-items-center bg-black/50 p-4"
      onMouseDown={(event) => {
        if (!busy && event.target === event.currentTarget) onClose();
      }}
    >
      <form
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={`${id}-description`}
        aria-busy={busy}
        className="w-full max-w-md rounded-xl border border-stone-200 bg-white p-5 shadow-2xl dark:border-white/10 dark:bg-stone-900"
        onSubmit={(event) => {
          event.preventDefault();
          if (!busy && (!action.input || value.trim())) onSubmit();
        }}
      >
        <h2 id={`${id}-title`} className="text-base font-semibold">
          {action.title}
        </h2>
        <p
          id={`${id}-description`}
          className="mt-2 text-sm break-words text-stone-600 dark:text-stone-300"
        >
          {action.description}
        </p>
        {action.input && (
          <label className="mt-4 block text-xs font-semibold">
            {t("New branch name")}
            <input
              ref={input}
              className="field mt-2 w-full"
              value={value}
              disabled={busy}
              onChange={(event) => onChange(event.target.value)}
            />
          </label>
        )}
        {error && (
          <p
            role="alert"
            className="mt-3 text-xs whitespace-pre-wrap text-red-600 dark:text-red-300"
          >
            {error}
          </p>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            className="button"
            disabled={busy}
            onClick={onClose}
          >
            {t("Cancel")}
          </button>
          <button
            type="submit"
            className={
              action.destructive
                ? "button bg-red-600 text-white hover:bg-red-700"
                : "button-primary"
            }
            disabled={busy || (action.input && !value.trim())}
          >
            {busy && (
              <LoaderCircle size={14} className="motion-safe:animate-spin" />
            )}
            {action.title}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
